import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { readCart, normalizeCart, cartStorageKey } from '../lib/cartStorage';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user, loading } = useAuth();
  const storageKey = loading ? null : cartStorageKey(user);
  // Remount before rendering a different identity. Old callbacks cannot mutate
  // the new account, and authentication loading never exposes a stored cart.
  return <ScopedCartProvider key={storageKey || 'auth-loading'} storageKey={storageKey}>{children}</ScopedCartProvider>;
}

function ScopedCartProvider({ children, storageKey }) {
  const [items, setItems] = useState(() => storageKey ? readCart(storageKey) : []);
  const [storageAvailable, setStorageAvailable] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lastAdded, setLastAdded] = useState(null); // { sku, at } - drives badge spring + first-add drawer
  useEffect(() => {
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, JSON.stringify(items)); setStorageAvailable(true); }
    catch { setStorageAvailable(false); }
  }, [items, storageKey]);

  const addItem = useCallback((product, quantity = 1) => {
    if (!storageKey) return;
    if (!normalizeCart([{ ...product, product_id: product.id, quantity }]).length) return;
    setItems(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      if (existing) {
        return normalizeCart(prev.map(i => i.product_id === product.id ? { ...i, quantity: i.quantity + Number(quantity) } : i));
      }
      return [...prev, {
        product_id: product.id,
        sku: product.sku,
        name: product.name,
        specification: product.specification,
        price: Number(product.price),
        weight_oz: product.weight_oz ?? null,
        quantity: Number(quantity),
      }];
    });
    setLastAdded({ sku: product.sku, at: Date.now() });
    // First add of the session opens the drawer once so the user sees the cart
    // state + min-order progress. After that, the badge spring + toast carry it.
    try {
      if (!sessionStorage.getItem(`${storageKey}:drawer-seen`)) {
        sessionStorage.setItem(`${storageKey}:drawer-seen`, '1');
        setDrawerOpen(true);
      }
    } catch (_) { setDrawerOpen(true); }
  }, [storageKey]);

  const updateQuantity = useCallback((productId, quantity) => {
    if (!Number.isSafeInteger(Number(quantity))) return;
    quantity = Number(quantity);
    if (quantity <= 0) {
      setItems(prev => prev.filter(i => i.product_id !== productId));
    } else {
      setItems(prev => prev.map(i => i.product_id === productId ? { ...i, quantity } : i));
    }
  }, []);

  const removeItem = useCallback((productId) => {
    const removed = items.find(item => item.product_id === productId);
    setItems(prev => prev.filter(i => i.product_id !== productId));
    if (removed) toast('Product removed', { action: { label: 'Undo', onClick: () => {
      setItems(prev => prev.some(item => item.product_id === productId) ? prev : [...prev, removed]);
    } } });
  }, [items]);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + (i.price * i.quantity), 0);

  return (
    <CartContext.Provider value={{ items, addItem, updateQuantity, removeItem, clearCart, totalItems, subtotal, storageAvailable, drawerOpen, setDrawerOpen, lastAdded }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
}
